import { FlaskConical } from 'lucide-react';

import { cn, createTestIdHelper } from '../../../lib/utils';

export const environmentBannerTestId = createTestIdHelper('environment-banner');

export type EnvironmentBannerProps = {
  /**
   * The environment name to show, already localized by the calling app (e.g.
   * "Staging"). A blank label renders nothing, so a misconfigured environment
   * fails closed instead of showing an empty bar.
   */
  label: string;
  /**
   * Optional trailing context, e.g. "This environment is used only for
   * testing purposes". Any punctuation joining it to the label belongs in
   * the label itself, so a locale keeps control of its own word order.
   */
  description?: string;
  className?: string;
};

/**
 * EnvironmentBanner
 *
 * A persistent, non-interactive strip pinned to the top of the viewport naming
 * the environment the app runs in, so test data and workflows are never
 * mistaken for production ones.
 *
 * Deciding WHETHER to render is the calling app's job — it owns the environment
 * flag; this component only renders the strip. It reserves its own space via the
 * `--env-banner-height` variable the app sets before React mounts; see the
 * `env-banner` / `app-viewport` tokens in the Tailwind preset and the `body`
 * margin rule in `globals.css`.
 *
 * Deliberately NOT an `aria-live` region: the banner is permanent state, not an
 * announcement, and a live region would re-announce it on every render. The
 * environment is carried by text, never by color alone.
 *
 * Styled in the Heart+ teal with a flask glyph rather than an alert
 * amber/triangle: a non-production environment is a fact about where you are,
 * not a warning about something being wrong, and warning chrome here would
 * compete with the genuine clinical alerts the product raises.
 */
export const EnvironmentBanner = ({
  label,
  description,
  className,
}: EnvironmentBannerProps) => {
  if (label.trim() === '') return null;

  return (
    <div
      role="note"
      data-testid={environmentBannerTestId('container')}
      // z-env-banner (60) outranks the modal layer (dialog/sheet overlays are
      // z-50) as well as the sidebar (z-10) and header (z-30), so the strip is
      // never dimmed or covered — FR-012.
      className={cn(
        // pointer-events-none: the strip carries no controls, and at z-60 it
        // would otherwise swallow clicks in the top 1.75rem — including the
        // close button of every right-side Sheet, which sits at top-4.
        // `min-h-7` (1.75rem) is a floor, not a duplicate: `h-env-banner` is
        // `var(--env-banner-height, 0rem)`, so if an app renders the banner
        // without calling `reserveEnvironmentBannerSpace` the strip would be
        // 0px tall -- painting no background while the text still overflows.
        // With the floor, a missing variable degrades to a correct-looking
        // strip. This also covers consumers installing from the shadcn
        // registry, which ships the component without the token definitions.
        'pointer-events-none fixed inset-x-0 top-0 z-env-banner flex h-env-banner min-h-7 items-center',
        'justify-center gap-2 px-4',
        // primary-700, not primary: white on primary-600 (#009999) is only
        // 3.49:1, under the 4.5:1 WCAG 2.2 AA floor for this 12px text.
        // primary-700 (#257F7E) gives 4.76:1 and is not remapped per theme, so
        // one value passes in both light and dark.
        'bg-primary-700 text-white',
        'text-xs font-semibold uppercase tracking-wide',
        className,
      )}
    >
      <FlaskConical
        className="size-3.5 shrink-0 opacity-80"
        aria-hidden="true"
      />
      <span data-testid={environmentBannerTestId('label')}>{label}</span>
      {description ? (
        <span
          data-testid={environmentBannerTestId('description')}
          // Hidden rather than truncated on narrow viewports: a clipped
          // "not for l…" is worse than just the label. No opacity — it would
          // drop the contrast back below AA.
          className="hidden font-normal normal-case tracking-normal sm:inline"
        >
          {description}
        </span>
      ) : null}
    </div>
  );
};
